import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Wallet } from 'lucide-react';
import { formatUZS } from '@/lib/format';
import {
  INCOME_SOURCES,
  IncomeRow,
  emptyIncomeRow,
  incomeTotal,
} from '@/lib/income-sources';

interface IncomeStepProps {
  rows: IncomeRow[];
  onChange: (rows: IncomeRow[]) => void;
}

/** Daromad qadami — mijozning qo'shimcha oylik daromad manbalari */
export function IncomeStep({ rows, onChange }: IncomeStepProps) {
  const { t } = useTranslation();
  const update = (idx: number, patch: Partial<IncomeRow>) => {
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const addRow = () => onChange([...rows, emptyIncomeRow()]);
  const removeRow = (idx: number) => onChange(rows.filter((_, i) => i !== idx));

  const total = incomeTotal(rows);

  return (
    <div className="space-y-5">
      {rows.map((row, idx) => (
        <div key={idx} className="rounded-xl border border-border p-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-muted-foreground">{t('pos_income.source_n', { n: idx + 1 })}</p>
            {rows.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeRow(idx)}
                className="text-destructive h-9"
              >
                <Trash2 size={16} className="mr-1" /> {t('pos_income.remove')}
              </Button>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-sm">{t('pos_income.type')}</Label>
              <Select value={row.source_code || '__none__'} onValueChange={(v) => update(idx, { source_code: v === '__none__' ? '' : v })}>
                <SelectTrigger className="h-14 text-base">
                  <SelectValue placeholder={t('pos_income.select')} />
                </SelectTrigger>
                <SelectContent>
                  {INCOME_SOURCES.map(s => (
                    <SelectItem key={s.code} value={s.code} className="text-base">{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">{t('pos_income.monthly')}</Label>
              <Input
                inputMode="numeric"
                value={row.monthly_amount}
                onChange={(e) => update(idx, { monthly_amount: e.target.value.replace(/[^\d]/g, '') })}
                placeholder="1500000"
                className="h-14 text-lg font-mono"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm">
              {t('pos_income.note')} {row.source_code === 'boshqa' ? t('pos_income.required') : t('pos_income.optional')}
            </Label>
            <Input
              value={row.note}
              onChange={(e) => update(idx, { note: e.target.value })}
              placeholder={t('pos_income.note_placeholder')}
              className="h-14 text-base"
            />
          </div>
        </div>
      ))}

      <Button variant="outline" onClick={addRow} className="w-full h-14 text-base">
        <Plus size={18} className="mr-2" /> {t('pos_income.add')}
      </Button>

      <div className="flex items-center justify-between rounded-xl bg-muted/50 p-4">
        <span className="flex items-center gap-2 text-base text-muted-foreground">
          <Wallet size={18} /> {t('pos_income.total')}
        </span>
        <span className="text-xl font-semibold tabular-nums text-foreground">{formatUZS(total)}</span>
      </div>
    </div>
  );
}
